// Reduction registry helpers (Stage 6 — TASKS-UI.md).
//
// Mirrors src/store/selections.ts shape: CRUD over `params.reductions` +
// `params.reductionOrder` + `params.nextReductionSeq`, plus focus.
// Compute results are NOT stored here — they live on a viewer cache in
// src/app/intents/reductions.ts because results derive from the spec
// and the current field array, not from independent state.
//
// Subscription pattern matches selections: callers listen on
// `params.reductionOrder` for registry shape changes and on
// `params.reductions.<id>` for individual edits.

import { params, store } from './instance';
import type { Reduction, ReductionId } from './schema';

// ─── id minting ────────────────────────────────────────────────────────────

function mintId(): ReductionId {
  const seq = params.nextReductionSeq;
  store.set('params.nextReductionSeq', seq + 1);
  return `red-${seq}`;
}

function nextLabel(): string {
  return `Reduction ${params.reductionOrder.length + 1}`;
}

// ─── defaults / normalisation ──────────────────────────────────────────────

function normaliseAxes(axes: ReadonlyArray<'x' | 'y' | 'z'> | undefined): ReadonlyArray<'x' | 'y' | 'z'> {
  if (!axes || axes.length === 0) return ['x', 'y', 'z'];
  const out: Array<'x' | 'y' | 'z'> = [];
  for (const a of ['x', 'y', 'z'] as const) {
    if (axes.includes(a)) out.push(a);
  }
  return out;
}

// ─── CRUD ──────────────────────────────────────────────────────────────────

export interface CreateReductionOpts {
  field?: string;
  op?: Reduction['op'];
  axes?: ReadonlyArray<'x' | 'y' | 'z'>;
  domain?: Reduction['domain'];
  timestepPolicy?: Reduction['timestepPolicy'];
  label?: string;
  focus?: boolean;
}

export function createReduction(opts: CreateReductionOpts = {}): ReductionId {
  const id = mintId();
  const reduction: Reduction = {
    id,
    label: opts.label ?? nextLabel(),
    field: opts.field ?? 'magnitude',
    op: opts.op ?? 'mean',
    axes: normaliseAxes(opts.axes),
    domain: opts.domain ?? 'full',
    timestepPolicy: opts.timestepPolicy ?? 'follow',
  };
  store.set(`params.reductions.${id}`, reduction);
  store.set('params.reductionOrder', [...params.reductionOrder, id]);
  if (opts.focus !== false) store.set('params.focusedReductionId', id);
  return id;
}

export type ReductionPatch = Partial<Omit<Reduction, 'id'>>;

export function updateReduction(id: ReductionId, patch: ReductionPatch): void {
  const cur = params.reductions[id];
  if (!cur) return;
  const next: Reduction = {
    ...cur,
    ...(typeof patch.label === 'string' ? { label: patch.label } : {}),
    ...(typeof patch.field === 'string' ? { field: patch.field } : {}),
    ...(patch.op ? { op: patch.op } : {}),
    ...(patch.axes ? { axes: normaliseAxes(patch.axes) } : {}),
    ...(patch.domain !== undefined ? { domain: patch.domain } : {}),
    ...(patch.timestepPolicy ? { timestepPolicy: patch.timestepPolicy } : {}),
  };
  store.set(`params.reductions.${id}`, next);
}

export function removeReduction(id: ReductionId): void {
  if (!params.reductions[id]) return;
  const reg = store.state.params.reductions as Record<string, Reduction>;
  delete reg[id];
  store.set('params.reductionOrder', params.reductionOrder.filter((x) => x !== id));
  if (params.focusedReductionId === id) store.set('params.focusedReductionId', null);
}

export function setFocus(id: ReductionId | null): void {
  if (id !== null && !params.reductions[id]) return;
  store.set('params.focusedReductionId', id);
}

/** Find all reductions whose domain references the given selection id. */
export function reductionsReferencing(selectionId: string): ReductionId[] {
  const out: ReductionId[] = [];
  for (const id of params.reductionOrder) {
    const r = params.reductions[id];
    if (r && r.domain === selectionId) out.push(id);
  }
  return out;
}

/**
 * The single reduction bound to a domain (`'full'` or a SelectionId), or null.
 * Reductions are one-per-domain; the first match is the only match.
 */
export function reductionForDomain(domainId: string): ReductionId | null {
  return reductionsReferencing(domainId)[0] ?? null;
}
